/**
 * A saved session with no secret-bearing target text (security slice 2026-09-28, `Docs/ConnectionSecrets.md`): its
 * name and every tab's title / connection name / host, as the workspace store's `safeTab` makes them. Applied when
 * sessions an older build persisted are rehydrated; the store then rewrites those fields in the file once (Fable review
 * S6; review round 2 — `rewritePersistedState`, nothing else in the file changes).
 */
import { safeTab, safeTabTitle } from '../../workspace/core/store/safeTabText';
import type { Session } from '../types/sessionTypes';
import { keepRecords } from '../../../core/storage/rewritePersistedState';

export function safeSession<T extends Pick<Session, 'name' | 'tabs'>>(session: T): T {
  // Total on any shape (review round 2): a malformed record is sanitized around, never aborts the rest.
  if (!session || typeof session !== 'object') return session;
  const name = safeTabTitle(session.name);
  if (session.tabs === undefined) return name === session.name ? session : { ...session, name };
  // Tabs that are not a list, or a tab that is not a record, cannot be read, so they are not kept (review rounds 5–6).
  if (!Array.isArray(session.tabs)) return { ...session, name, tabs: [] };
  const tabs = keepRecords(session.tabs).map((tab) => safeTab(tab));
  const unchanged = name === session.name && tabs.length === session.tabs.length && tabs.every((tab, index) => tab === session.tabs[index]);
  return unchanged ? session : { ...session, name, tabs };
}
